import React, { ChangeEvent, useEffect } from 'react';
import { RegisterOptions } from 'react-hook-form';
import has from 'lodash/has';
import get from 'lodash/get';
import FormInputStyleProperties from '@app/component/form/input/FormInputStyleProperties';
import { useTranslation } from '@app/component/translation/Translation';
import Utilities from '@util/Utilities';
import _uniqueId from 'lodash/uniqueId';
import { MinusIcon, PlusIcon } from '@heroicons/react/20/solid';

type Props = {
	isRequired?: boolean,
	placeholder?: string,
	form?: any,
	name: string,
	label?: string,
	labelHint?: React.JSX.Element,
	disabled?: boolean,
	className?: string,
	hideErrorMessage?: boolean,
	min?: number,
	max?: number,
	showControls?: boolean,
	onChange?: (value: number | undefined) => any
} & FormInputStyleProperties

const FormInputNumber: React.FunctionComponent<Props> = (
	{
		form,
		placeholder,
		name,
		isRequired = false,
		label,
		isLoading,
		disabled = false,
		className,
		labelHint,
		hideErrorMessage = false,
		min,
		max,
		showControls = false,
		onChange,
	}) => {

	const { t } = useTranslation('common.form.error');
	const hasError = !!form && has(form.formState.errors, name);
	const errorMessage = hasError ? (get(form.formState.errors, name + '.message') || t('required')) : '';

	const _onChange = (event: ChangeEvent<HTMLInputElement>) => {
		if (!!onChange) {
			if (!event.target.value) onChange(undefined);
			else onChange(parseInt(event.target.value));
		}
	};

	const options: Partial<RegisterOptions> = {
		required: isRequired,
		onBlur: _onChange,
	};
	const uniqueId = _uniqueId('input-');

	const value = form.watch(name);

	useEffect(() => {
		if (parseInt(value) < (min ?? 1)) form.setValue(name, min ?? 1);
		if (parseInt(value) > (max ?? Number.MAX_SAFE_INTEGER)) form.setValue(name, max ?? Number.MAX_SAFE_INTEGER);
	}, [value]);

	const decrease = () => {
		if (!!value && parseInt(value) > (min ?? 1))
			form.setValue(name, parseInt(value) - 1);
	};

	const increase = () => {
		if (!!value && parseInt(value) < (max ?? Number.MAX_SAFE_INTEGER))
			form.setValue(name, parseInt(value) + 1);
	};


	const renderField = () => {
		return <>
			{!!form && <input id={uniqueId} type="number"
												className={Utilities.classNames(
													!!className ? className : 'min-w-0 flex-1 input',
													Utilities.NVL(hasError, 'input-error'),
													Utilities.NVL(isLoading, 'input-isLoading'),
													Utilities.NVL(disabled, 'input-disabled'),
												)}
												onBlur={_onChange}
												disabled={disabled}
												placeholder={placeholder} {...form.register(name, options)} />}
			{!form && <input id={uniqueId} name={name} type="number"
											 className={Utilities.classNames(
												 !!className ? className : 'min-w-0 flex-1 input',
												 Utilities.NVL(hasError, 'input-error'),
												 Utilities.NVL(isLoading, 'input-isLoading'),
												 Utilities.NVL(disabled, 'input-disabled'),
											 )}
											 placeholder={placeholder}
											 onBlur={_onChange}
											 disabled={disabled} />}

		</>;
	};

	return <>
		{!!label && <div className="label">{label}</div>}
		{!!labelHint ? labelHint : ''}
		<div className="mt-0.5 flex rounded-md shadow-sm">
			{showControls && <button type="button"
															 className="inline-flex items-center rounded-l-md border border-r-0 border-slate-300 px-3 text-slate-500 sm:text-sm focus:border-blue-500 focus:ring-blue-500 "
															 onClick={decrease}>
				<MinusIcon className="h-5 w-5 text-slate-400" />
			</button>}
			{renderField()}
			{showControls && <button type="button"
															 className="inline-flex items-center rounded-r-md border border-l-0 border-slate-300 px-3 text-slate-500 sm:text-sm focus:border-blue-500 focus:ring-blue-500 "
															 onClick={increase}>
				<PlusIcon className="h-5 w-5 text-slate-400" />
			</button>}
		</div>
		{!!errorMessage && !hideErrorMessage && <p className="help help-error">{errorMessage}</p>}
	</>;

};

export default FormInputNumber;